import { defineComponent, onMounted, ref } from "vue";
import * as Cookies from "js-cookie";

type ConsentStatus = "accepted" | "rejected";

interface ConsentData {
  consent_status: ConsentStatus;
  consent_timestamp: number;
  consent_version?: string;
}

const CONSENT_COOKIE_KEY = "GrapeSeed.CookieConsent";
const CONSENT_COOKIE_VERSION = "";

function isRussiaDomain() {
  return /\.ru$/i.test(window.location.hostname);
}


function normalizeConsentVersion(version?: string): string | null {
  if (version === undefined || version === null) return null;

  const normalized = String(version).trim();
  return normalized ? normalized : null;
}

function parseConsentData(raw: string | null): ConsentData | null {
  if (!raw) return null;

  try {
    const data = JSON.parse(raw) as ConsentData;
    const status = data && data.consent_status;
    if (!status) return null;

    const normalized = String(status).toLowerCase();
    if (normalized !== "accepted" && normalized !== "rejected") return null;

    return {
      consent_status: normalized as ConsentStatus,
      consent_timestamp: data && typeof data.consent_timestamp === "number" ? data.consent_timestamp : 0,
      consent_version: normalizeConsentVersion(data && data.consent_version) || undefined,
    };
  } catch (error) {
    return null;
  }
}

function shouldRequireConsentRenewal(consentData: ConsentData, consentVersion?: string): boolean {
  const currentVersion = normalizeConsentVersion(consentVersion);
  if (!currentVersion) return false;

  const confirmedVersion = normalizeConsentVersion(consentData && consentData.consent_version);
  return confirmedVersion !== currentVersion;
}

function setConsentCookie(status: ConsentStatus, consentVersion?: string) {
  const data: ConsentData = {
    consent_status: status,
    consent_timestamp: Date.now(),
  };
  const normalizedVersion = normalizeConsentVersion(consentVersion);
  if (normalizedVersion) {
    data.consent_version = normalizedVersion;
  }

  const domain = process.env.VUE_APP_PORTAL_DOMAIN;
  const hostname = window.location.hostname;
  const shouldSetDomain = !!domain && (hostname === domain || hostname.endsWith(`.${domain}`));

  Cookies.set(CONSENT_COOKIE_KEY, JSON.stringify(data), {
    expires: 365,
    path: "/",
    ...(shouldSetDomain ? { domain } : {}),
  });
}

function deleteGACookies() {
  const domain = "." + window.location.hostname.split(".").slice(-2).join(".");
  const expired = "expires=Thu, 01 Jan 1970 00:00:00 GMT";

  document.cookie.split(";").forEach((cookieEntry) => {
    const name = cookieEntry.split("=")[0].trim();
    if (name === "_ga" || name === "_gid" || name === "_gat" || name.indexOf("_ga_") === 0) {
      document.cookie = `${name}=;${expired};path=/`;
      document.cookie = `${name}=;${expired};path=/;domain=${domain}`;
    }
  });
}

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

export default defineComponent({
  name: "CookieConsentBanner",
  setup() {
    const visible = ref(false);
    const privacyPolicyUrl = `${process.env.VUE_APP_AUTHORITY_URL}/Copyright/privacy-policy`;

    onMounted(() => {
      if (!isRussiaDomain()) return;

      const isOidcCallback = window.location.pathname.includes("/oidc/") || window.location.hash.includes("id_token");
      if (isOidcCallback) return;

      const consent = parseConsentData(Cookies.get(CONSENT_COOKIE_KEY) || null);
      if (consent && consent.consent_status === "rejected") {
        deleteGACookies();
      }

      if (consent === null || shouldRequireConsentRenewal(consent, CONSENT_COOKIE_VERSION)) {
        visible.value = true;
        return;
      }

    });

    const accept = () => {
      setConsentCookie("accepted", CONSENT_COOKIE_VERSION);
      visible.value = false;
      window.location.reload();
    };

    const reject = () => {
      setConsentCookie("rejected", CONSENT_COOKIE_VERSION);
      deleteGACookies();
      visible.value = false;
    };

    return {
      visible,
      privacyPolicyUrl,
      accept,
      reject,
    };
  },
});
